import { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { fetchPatientDetailsUuid, postPatientReport } from '../Redux/Thunk/Actions';
import '../styles/PatientReportFileTemplateCreate.scss';
import AnalysisTable from './AnalysisTable';
import FigmaHeader from './FigmaHeader/FigmaHeader';
import MedicalReportTemplate from './MedicalReportTemplate';
import { RenderSignature } from '../utils/templateUtils';

const isValidBranchAwareUhid = value => /^(ASCAS|VDASCAS)\d+$/i.test((value || '').trim());

// Common styles
const commonStyles = {
  input: {
    border: '1px solid #b0a4a4ff',
    outline: 'none',
    padding: '10px',
    backgroundColor: 'white'
  },
  label: {
    fontWeight: 600
  },
  separator: {
    fontWeight: 600,
    fontSize: '18px'
  },
  section: {
    maxWidth: '97%'
    // margin: '0 auto'
  }
};

// Reusable FormField component
const FormField = ({label, type = 'text', name, value, onChange, show, options = [], width = '200px', labelWidth = '160px', reference}) => {
  const inputStyle = {...commonStyles.input, width};

  if (show) {
    // Display mode (read-only)
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `${labelWidth} 16px 1fr`,
          alignItems: 'center',
          gap: '16px'
        }}>
        <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>{label}</span>
        <span style={commonStyles.separator}>:</span>
        <span style={{fontSize: '14px', textAlign: 'left'}}>{value}</span>
      </div>
    );
  }

  // Edit mode
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${labelWidth} 16px ${width}`,
        alignItems: 'center',
        gap: '16px'
      }}>
      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px'}}>{label}</span>
      <span style={commonStyles.separator}>:</span>

      {/* Input for editing mode */}
      {type === 'select' ? (
        <select name={name} value={value} onChange={onChange} style={inputStyle}>
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input type={type} name={name} value={value} onChange={onChange} style={inputStyle} />
      )}
    </div>
  );
};

// FormRow for grid layout with columns
const FormRow = ({columns, show, onChange, valueChange, className = 'report-template-wrap2', columnGap = '40px', defaultLabelWidth = '160px'}) => (
  <div className={className} style={{padding: '3px 6px', display: 'flex', gap: columnGap}}>
    {columns.map((colFields, colIndex) => (
      <div
        key={colIndex}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '3px'
        }}>
        {colFields.map(field => (
          <FormField
            key={field.name}
            label={field.label}
            type={field.type || 'text'}
            name={field.name}
            value={valueChange[field.name]}
            onChange={onChange}
            show={show}
            options={field.options}
            width={field.width || '100%'}
            labelWidth={field.labelWidth || defaultLabelWidth}
            reference={field.reference}
          />
        ))}
      </div>
    ))}
  </div>
);

// MacroscopicField component for result + reference layout
const MacroscopicField = ({label, name, value, onChange, show, type = 'text', options = [], width = '200px', reference}) => {
  const inputStyle = {...commonStyles.input, width};

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 200px 160px', // Label | Input/Value | Reference
        gap: '16px',
        alignItems: 'center',
        padding: '8px 0',
        width: '100%'
      }}>
      {/* Parameter Label */}
      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px', textAlign: 'left'}}>{label}</span>

      {/* Input Field or Display Value */}
      {show ? (
        // Display mode
        <span style={{fontSize: '14px', textAlign: 'left'}}>{value || ''}</span>
      ) : (
        // Edit mode
        <>
          {type === 'select' ? (
            <select name={name} value={value} onChange={onChange} style={inputStyle}>
              {options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          ) : (
            <input type={type} name={name} value={value} onChange={onChange} style={inputStyle} />
          )}
        </>
      )}

      {/* WHO Reference (Always visible) */}
      <span style={{textAlign: 'left', fontSize: '14px', fontWeight: '500', color: '#333'}}>{reference || ''}</span>
    </div>
  );
};

// Section header component
const SectionHeader = ({title, className = 'spermiogram-head'}) => (
  <h2
    style={{
      textAlign: 'center',
      backgroundColor: '#f7f7f7',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      fontSize: '20px',
      fontWeight: '600',
      padding: '8px 0 8px 10px',
      margin: '8px 0'
    }}
    className={className}>
    {title}
  </h2>
);

// Column headers for tables
const TableHeaders = ({columns, colWidths = '200px 230px 200px', gapSize = '0px'}) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: colWidths,
      alignItems: 'center',
      gap: gapSize,
      overflowX: 'hidden',
      maxWidth: '97%',
      margin: '0 auto',
      backgroundColor: '#e9ecef',
      padding: '8px 16px',
      borderBottom: '1px solid #ddd'
    }}>
    {columns.map((col, index) => (
      <span
        key={index}
        style={{
          fontWeight: '600',
          fontSize: '14px',
          textAlign: index === 1 ? 'center' : 'left'
        }}>
        {col}
      </span>
    ))}
  </div>
);

// MicroscopyField component for pre/post wash parameters - Consistent with FormField styling
const MicroscopyField = ({label, name, reference, show, valueChange, onChange, width = '140px'}) => {
  const preInputWidth = width; // Configurable pre-wash input width (for volume field)
  const postInputWidth = '200px'; // Standard input width for post-wash

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `240px 16px ${preInputWidth} ${postInputWidth} ${preInputWidth} ${postInputWidth} 120px`,
        gap: '8px',
        alignItems: 'center',
        padding: '8px 0',
        width: '100%'
      }}>
      {/* Parameter Label */}
      <span style={{...commonStyles.label, whiteSpace: 'nowrap', fontSize: '14px', textAlign: 'left'}}>{label}</span>

      {/* Separator */}
      <span style={commonStyles.separator}>:</span>

      {/* Pre-wash input/display */}
      {show ? (
        // Display mode
        <span style={{width: preInputWidth, textAlign: 'center', fontSize: '14px'}}>{valueChange[name] || ''}</span>
      ) : (
        // Edit mode
        <input type='text' onChange={onChange} name={name} value={valueChange[name]} style={{...commonStyles.input, width: preInputWidth}} />
      )}

      {/* Post-wash input/display */}
      {show ? (
        // Display mode
        <span style={{width: postInputWidth, textAlign: 'center', fontSize: '14px'}}>{valueChange[`${name}Post`] || ''}</span>
      ) : (
        // Edit mode
        <input type='text' onChange={onChange} name={`${name}Post`} value={valueChange[`${name}Post`]} style={{...commonStyles.input, width: postInputWidth}} />
      )}

      {/* Pre-wash display (hidden in display mode) */}
      <span className='input hide' style={{width: preInputWidth, textAlign: 'center', fontSize: '14px'}}>
        {valueChange[name]}
      </span>

      {/* Post-wash display (hidden in display mode) */}
      <span className='input hide' style={{width: postInputWidth, textAlign: 'center', fontSize: '14px'}}>
        {valueChange[`${name}Post`]}
      </span>

      {/* WHO Reference */}
      <span style={{textAlign: 'left', fontSize: '14px', fontWeight: '500'}}>{reference || ''}</span>
    </div>
  );
};

// Microscopy fields configuration - Updated per iui.docx specification
const microscopyFields = [
  {label: 'Volume (mL)', name: 'volume', reference: '>= 1.4mL', width: '200px'},
  {label: 'Sperm Concentration (mill/mL)', name: 'concentration', reference: '>= 16 million/mL', width: '200px'},
  {label: 'Total Sperm Count (million)', name: 'total', reference: '>= 39 million', width: '200px'},
  {label: 'Progressive Motility (%)', name: 'progressiveMotility', reference: '>= 30%', width: '200px'},
  {label: 'Non-Progressive Motility (%)', name: 'nonProgressiveMotile', width: '200px'},
  {label: 'Immotile (%)', name: 'immotile', width: '200px'},
  {label: 'Morphology (Strict Criteria)', name: 'morphology', reference: '>= 4% normal forms', width: '200px'},
  {label: 'Total Motile Sperm Count (TMSC)', name: 'totalMotileSpermCount', reference: '>=42%', width: '200px'}
];

// Method dropdown options - Updated per iui.docx specification
const methodOptions = [
  {value: '', label: 'Select'},
  {value: 'DGC', label: 'DGC'},
  {value: 'DGC+SWIM UP', label: 'DGC+SWIM UP'},
  {value: 'SWIM UP', label: 'SWIM UP'},
  {value: 'MICROFLUIDCS', label: 'MICROFLUIDCS'}
];

// Patient Info Section component
const PatientInfoSection = ({patientNumber, partnerType, show, handleChange, valueChange}) => {
  const genderOptions = [
    {value: '', label: 'Select Gender'},
    {value: 'Male', label: 'Male'},
    {value: 'Female', label: 'Female'},
    {value: 'Others', label: 'Others'},
    {value: 'Decline to State', label: 'Decline to State'}
  ];

  return (
    <div className='' style={{width: '100%', display: 'block'}} id={`patientInfo${patientNumber}`}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: '3px',
          backgroundColor: '#f7f7f7 ',
          borderRadius: '6px',
          padding: '12px 6px 12px 6px',
          gap: '3px'
        }}>
        {/* Patient ID */}
        <FormField label='Patient ID' name={`patientId${patientNumber}`} value={valueChange[`patientId${patientNumber}`]} onChange={handleChange} show={show} width='250px' labelWidth='125px' />

        {/* Patient Name */}
        <FormField
          label={`${partnerType} Partner Name`}
          name={`patientName${patientNumber}`}
          value={valueChange[`patientName${patientNumber}`]}
          onChange={handleChange}
          show={show}
          width='250px'
          labelWidth='125px'
        />

        {/* Gender */}
        <FormField
          label='Gender'
          type='select'
          name={`gender${patientNumber}`}
          value={valueChange[`gender${patientNumber}`]}
          onChange={handleChange}
          show={show}
          options={genderOptions}
          width='250px'
          labelWidth='125px'
        />

        {/* Age */}
        <FormField label='Age' name={`age${patientNumber}`} value={valueChange[`age${patientNumber}`]} onChange={handleChange} show={show} width='250px' labelWidth='125px' />
      </div>
    </div>
  );
};

const IUIReport = props => {
  const [show, setshow] = useState(false);
  const Content = useRef();
  const [valueChange, setvalueChange] = useState({
    gender1: 'Male',
    gender2: 'Female',
    bloodGroup1: 'A+',
    bloodGroup2: 'A+',
    coupleId: '',
    collectionDate: '',
    resultDate: '',
    procedureRequested: '',
    sample: '',
    abstinencePeriod: '',
    collectionMethod: '',
    collectionDifficulty: '',
    pH: '',
    timeOfCollection: '',
    timeOfCollectionHour: '',
    timeOfCollectionMinute: '',
    timeOfCollectionPeriod: 'AM',
    timeOfExamination: '',
    timeOfExaminationHour: '',
    timeOfExaminationMinute: '',
    timeOfExaminationPeriod: 'AM',
    appearance: '',
    color: '',
    homogeneity: '',
    completeSample: '',
    liquefactionTime: '',
    volume: '',
    volumePost: '',
    concentration: '',
    concentrationPost: '',
    total: '',
    totalPost: '',
    progressiveMotility: '',
    progressiveMotilityPost: '',
    nonProgressiveMotile: '',
    nonProgressiveMotilePost: '',
    immotile: '',
    immotilePost: '',
    totalMotileSpermCount: '',
    totalMotileSpermCountPost: '',
    method: '',
    methodType: '',
    morphology: '',
    morphologyPost: '',
    technician: '',
    technicianOtherName: '',
    comments: '',
    
    // Reporting
    reporting: {
      androtechName: '',
      consultantName: '',
      technician: '',
      technicianOtherName: '',
      clinicianSignature: ''
    }
  });

  // Calculate TMSC automatically for Pre and Post wash
  useEffect(() => {
    // Pre-wash calculation
    const volume = parseFloat(valueChange.volume) || 0;
    const concentration = parseFloat(valueChange.concentration) || 0;
    const progressiveMotility = parseFloat(valueChange.progressiveMotility) || 0;
    const nonProgressiveMotile = parseFloat(valueChange.nonProgressiveMotile) || 0;

    const tmsc = volume * ((progressiveMotility + nonProgressiveMotile) / 100) * concentration;
    const tmscRounded = tmsc > 0 ? tmsc.toFixed(2) : '';

    // Post-wash calculation
    const volumePost = parseFloat(valueChange.volumePost) || 0;
    const concentrationPost = parseFloat(valueChange.concentrationPost) || 0;
    const progressiveMotilityPost = parseFloat(valueChange.progressiveMotilityPost) || 0;
    const nonProgressiveMotilePost = parseFloat(valueChange.nonProgressiveMotilePost) || 0;

    const tmscPost = volumePost * ((progressiveMotilityPost + nonProgressiveMotilePost) / 100) * concentrationPost;
    const tmscPostRounded = tmscPost > 0 ? tmscPost.toFixed(2) : '';

    // Update state only if values have changed to avoid infinite loops
    if (tmscRounded !== valueChange.totalMotileSpermCount || tmscPostRounded !== valueChange.totalMotileSpermCountPost) {
      setvalueChange(prev => ({
        ...prev,
        totalMotileSpermCount: tmscRounded,
        totalMotileSpermCountPost: tmscPostRounded
      }));
    }
  }, [
    valueChange.volume,
    valueChange.concentration,
    valueChange.progressiveMotility,
    valueChange.nonProgressiveMotile,
    valueChange.volumePost,
    valueChange.concentrationPost,
    valueChange.progressiveMotilityPost,
    valueChange.nonProgressiveMotilePost
  ]);

  const [patientOne_UUId, setPatientOne_UUId] = useState('');
  const [patientTwo_UUId, setPatientTwo_UUId] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleChange = e => {
    var text = e.target.value;

    if (e.target.name === 'patientId1' || e.target.name === 'patientId2') {
      if (isValidBranchAwareUhid(e.target.value)) {
        const fetchData = async () => {
          const payload = {
            p_uhid: e.target.value
          };
          props.loaderchange('true');
          const addPatient = fetchPatientDetailsUuid(payload);
          const response = await dispatch(addPatient);
          if (response.payload?.status?.code === 200) {
            if (response.payload.data.data.length === 1) {
              const data = response.payload.data.data[0];
              const namePat1 = data?.titles + ' ' + data?.first_name + ' ' + data?.last_name;
              if (e.target.name === 'patientId1') {
                setPatientOne_UUId(data?.id);
                setvalueChange({
                  ...valueChange,
                  patientName1: namePat1,
                  age1: data?.age,
                  gender1: data?.gender,
                  patientId1: text
                });
              }
              if (e.target.name === 'patientId2') {
                setPatientTwo_UUId(data?.id);
                setvalueChange({
                  ...valueChange,
                  patientName2: namePat1,
                  age2: data?.age,
                  gender2: data?.gender,
                  patientId2: text
                });
              }

              props.loaderchange('false');
            } else {
              const data = response.payload.data.data[0].gender == 'Male' ? response.payload.data.data[0] : response.payload.data.data[1];
              const data2 = response.payload.data.data[1].gender == 'Female' ? response.payload.data.data[1] : response.payload.data.data[0];
              console.log(data, data2);
              const namePat1 = data?.titles + ' ' + data?.first_name + ' ' + data?.last_name;
              const namePat2 = data2?.titles + ' ' + data2?.first_name + ' ' + data2?.last_name;

              setPatientOne_UUId(data?.id);
              setPatientTwo_UUId(data2?.id);
              setvalueChange({
                ...valueChange,
                patientName1: namePat1,
                patientName2: namePat2,
                age1: data?.age,
                age2: data2?.age,
                gender1: data?.gender,
                gender2: data2?.gender,
                patientId1: data?.p_uhid,
                patientId2: data2?.p_uhid
              });

              props.loaderchange('false');
            }
          } else {
            props.loaderchange('false');
            props.popupalert('true');
            props.popuptext('Network went wrong, Please try after some time.');
            setTimeout(() => {
              props.popupalert('false');
            }, 2000);
          }
          return;
        };

        fetchData();
      } else {
        if (e.target.name === 'patientId1') {
          setvalueChange({
            ...valueChange,
            patientId1: e.target.value
          });
        }
        if (e.target.name === 'patientId2') {
          setvalueChange({
            ...valueChange,
            patientId2: e.target.value
          });
        }
      }
    } else if (
      e.target.name === 'androtechName' ||
      e.target.name === 'consultantName' ||
      e.target.name === 'technician' ||
      e.target.name === 'technicianOtherName' ||
      e.target.name === 'clinicianSignature'
    ) {
      setvalueChange({
        ...valueChange,
        reporting: {
          ...valueChange.reporting,
          [e.target.name]: e.target.value
        }
      });
    } else {
      setvalueChange({...valueChange, [e.target.name]: e.target.value});
    }
  };
  const handletest = async () => {
    try {
      if (
        valueChange.patientOne_UUId !== '' &&
        valueChange.patientId1 !== '' &&
        isValidBranchAwareUhid(valueChange.patientId1) &&
        (valueChange.patientId2 !== '' ? (valueChange.patientId1 !== valueChange.patientId2 ? true : false) : true)
      ) {
        setshow(true);
        // Set timeout to handle async report creation
        setTimeout(async () => {
          // Get HTML payload
          const htmlPayload = document.getElementById('reportcreate').innerHTML;

          // Prepare payload for creating report
          const payload = {
            report_template: htmlPayload,
            patient_id: patientOne_UUId,
            description: '',
            patient_two: patientTwo_UUId,
            couple_id: valueChange.coupleId,
            template_type: 'IUI Report'
          };

          // Dispatch action to create patient report
          props.loaderchange(true);
          const reportResponse = await dispatch(postPatientReport({type: '2', payload: payload}));

          if (reportResponse?.payload?.status?.code === 200) {
            props.loaderchange(false);
            navigate('/patientreports');

            // Show success message
            Swal.fire({
              title: 'Successful.',
              text: 'Report added successful.',
              icon: 'success',
              showConfirmButton: false,
              timer: 1500
            });
          } else {
            setshow(false);

            props.loaderchange(false);

            // Show error message
            props.popupalert(true);
            props.popuptext('Network went wrong, Please try after some time.');

            // Hide error message after timeout
            setTimeout(() => {
              props.popupalert(false);
            }, 2000);
          }
        }, 3000);
      } else {
        // Show error message for invalid patient ids
        Swal.fire({
          title: 'Error.',
          text: 'Please enter valid patient id.',
          icon: 'error',
          showConfirmButton: false,
          timer: 1500
        });
      }
    } catch (error) {
      console.error('An error occurred:', error);
    }
  };
  const sideactive = useSelector(state => state.counter.sidebarnav);

  const macroscopicRows = [
    {label: 'pH', name: 'pH', unit: 'pH', reference: '≥ 7.2'},
    {label: 'Appearance', name: 'appearance', reference: 'Grey-opalescent'},
    {label: 'Color', name: 'color', reference: 'Whitish-grey'},
    {label: 'Homogeneity', name: 'homogeneity', reference: 'Homogenous'},
    {label: 'Complete Sample', name: 'completeSample', reference: 'Yes / No'},
    {label: 'Liquefaction Time', name: 'liquefactionTime', unit: 'Mins', reference: '≤ 60 min'}
  ];

  const microscopicRows = [
    {label: 'Volume', namePre: 'volume', namePost: 'volumePost', unit: 'mL', reference: '≥ 1.4mL'},
    {label: 'Sperm Concentration', namePre: 'concentration', namePost: 'concentrationPost', unit: 'million/mL (×10⁶/mL)', reference: '≥ 16 million/mL'},
    {label: 'Total Sperm Count', namePre: 'total', namePost: 'totalPost', unit: 'Million', reference: '≥ 39 million'},
    {label: 'Progressive Motility', namePre: 'progressiveMotility', namePost: 'progressiveMotilityPost', unit: '%', reference: '≥ 30%'},
    {label: 'Non-Progressive Motility', namePre: 'nonProgressiveMotile', namePost: 'nonProgressiveMotilePost', unit: '%', reference: '1%'},
    {label: 'Immotile', namePre: 'immotile', namePost: 'immotilePost', unit: '%', reference: '20%'},
    {label: 'Morphology (Strict Criteria)', namePre: 'morphology', namePost: 'morphologyPost', unit: '%', reference: '≥ 4% normal forms'},
    {label: 'Total Motile Sperm Count (TMSC)', namePre: 'totalMotileSpermCount', namePost: 'totalMotileSpermCountPost', unit: 'Million', reference: '>=42%'},
    {label: 'Processing Method', namePre: 'method', namePost: 'methodType', typePre: 'select', type: 'select', options: methodOptions, width: '200px'}
  ];

  return (
    <>
      <div className={sideactive ? 'maincontainer-patient-sa' : 'maincontainer-patient'}>
        <div className='row'>
          <div className='col'>
            <h3 className='page-title'>Report Template</h3>
            <ul className='breadcrumb'>
              <li className='breadcrumb-item'>
                <a href='/' style={{textDecoration: 'none', color: 'black'}}>
                  Dashboard /
                </a>
                <a href='/patientreports' style={{textDecoration: 'none', color: 'black'}}>
                  &nbsp;Patient Reports
                </a>
              </li>
              <li className='breadcrumb-item active'>Report Template</li>
            </ul>
          </div>
        </div>
        {/* <h1 style={{textAlign: 'center'}}>🧬 IUI Report</h1> */}
        {/* <p style={{textAlign: 'center', fontStyle: 'italic', marginBottom: '20px'}}>As per WHO 6th Edition (2021) Guidelines</p> */}
        <div style={{backgroundColor: 'white', width: '100%'}} className='patientreport' id='reportcreate'>
          {show ? (
            <MedicalReportTemplate
              title='IUI REPORT'
              patientData={{
                patientOneId: valueChange.patientId1,
                patientTwoId: valueChange.patientId2,
                patientOneName: valueChange.patientName1,
                patientTwoName: valueChange.patientName2,
                genderOne: valueChange.gender1,
                genderTwo: valueChange.gender2,
                patientOneAge: valueChange.age1,
                patientTwoAge: valueChange.age2,
                date: new Date().toLocaleDateString('en-IN'),
                doctor: valueChange.reporting.consultantName,
                modeOfCollection: valueChange.sample,
                timeOfExamination: valueChange.timeOfExamination || '',
                completeCollection: valueChange.completeSample,
                sampleCollection: valueChange.collectionMethod,
                timeOfCollection: valueChange.timeOfCollection || '',
                sampleCollectionDate: valueChange.collectionDate || new Date().toLocaleDateString('en-IN'),
                abstinencePeriod: valueChange.abstinencePeriod ? `${valueChange.abstinencePeriod} Days` : '',
                technician: valueChange.reporting.technician,
                technicianOtherName: valueChange.reporting.technicianOtherName,
                androtechName: valueChange.reporting.androtechName,
                consultantName: valueChange.reporting.consultantName,
                clinicianSignature: valueChange.reporting.clinicianSignature,
                androtechSignature: ''
              }}
              sections={[
                {
                  title: 'Patient & Request Details',
                  layout: 'custom-grid',
                  data: [
                    // Left column - first 3 items
                    [
                      {label: 'Collection Date', value: valueChange.collectionDate || ''},
                      {label: 'Result Date', value: valueChange.resultDate || ''},
                      {label: 'Procedure Requested', value: valueChange.procedureRequested || ''}
                    ],
                    // Right column - remaining 3 items
                    [
                      {label: 'Sample Origin', value: valueChange.sample || ''},
                      {label: 'Collection Method', value: valueChange.collectionMethod || ''},
                      {label: 'Abstinence Period', value: valueChange.abstinencePeriod ? `${valueChange.abstinencePeriod} Days` : ''},
                      {label: 'Collection Difficulty', value: valueChange.collectionDifficulty || ''}
                    ]
                  ]
                },
                {
                  title: 'Macroscopic Examination',
                  layout: 'custom-grid',
                  data: [
                    // Left column - first 3 items
                    [
                      {label: 'pH', value: valueChange.pH ? `${valueChange.pH} pH` : ''},
                      {label: 'Appearance', value: valueChange.appearance || ''},
                      {label: 'Color', value: valueChange.color || ''}
                    ],
                    // Right column - remaining 3 items
                    [
                      {label: 'Homogeneity', value: valueChange.homogeneity || ''},
                      {label: 'Complete Sample', value: valueChange.completeSample || ''},
                      {label: 'Liquefaction Time', value: valueChange.liquefactionTime ? `${valueChange.liquefactionTime} Mins` : ''}
                    ]
                  ]
                },
                {
                  title: 'Microscopic Examination',
                  layout: 'full-table',
                  headers: ['Parameter', 'PRE WASH', 'POST WASH', 'WHO Reference'],
                  data: [
                    [
                      { value: 'Volume (mL)' },
                      { value: valueChange.volume || '-' },
                      { value: valueChange.volumePost || '-' },
                      { value: '≥ 1.4 mL' }
                    ],
                    [
                      { value: 'Sperm Concentration (million/mL)' },
                      { value: valueChange.concentration || '-' },
                      { value: valueChange.concentrationPost || '-' },
                      { value: '≥ 16 million/mL' }
                    ],
                    [
                      { value: 'Total Sperm Count (Million)' },
                      { value: valueChange.total || '-' },
                      { value: valueChange.totalPost || '-' },
                      { value: '≥ 39 Million' }
                    ],
                    [
                      { value: 'Progressive Motility (%)' },
                      { value: valueChange.progressiveMotility || '-' },
                      { value: valueChange.progressiveMotilityPost || '-' },
                      { value: '≥ 30%' }
                    ],
                    [
                      { value: 'Non-Progressive Motile (%)' },
                      { value: valueChange.nonProgressiveMotile || '-' },
                      { value: valueChange.nonProgressiveMotilePost || '-' },
                      { value: '-' }
                    ],
                    [
                      { value: 'Immotile (%)' },
                      { value: valueChange.immotile || '-' },
                      { value: valueChange.immotilePost || '-' },
                      { value: '-' }
                    ],
                    [
                      { value: 'Morphology (%)' },
                      { value: valueChange.morphology || '-' },
                      { value: valueChange.morphologyPost || '-' },
                      { value: '≥ 4% normal forms' }
                    ],
                    [
                      { value: 'Total Motile Sperm Count (Million)' },
                      { value: valueChange.totalMotileSpermCount || '-' },
                      { value: valueChange.totalMotileSpermCountPost || '-' },
                      { value: '>=42%' }
                    ],
                    [
                      { value: 'Processing Method' },
                      { value: valueChange.method || '-' },
                      { value: valueChange.methodType || '-' },
                      { value: '-' }
                    ]
                  ]
                },
              ]}
              impression=''
              comments={valueChange.comments}
              referenceValues={[]}
              footerText={`Lab Reference: IUI-2026-${new Date().toISOString().slice(0, 10).replace(/-/g, '')} | WHO Reference Values 2021 6th Edition`}
            />
          ) : (
            <>
              {/* Patient Information Header */}
              <FigmaHeader />

              {/* Patient & Request Details */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: '3px',
                  border: '1px solid #000'
                }}
                className='report-template-wrap1'>
                <PatientInfoSection patientNumber={1} partnerType='Male' show={show} handleChange={handleChange} valueChange={valueChange} />
                <div className='patientreportcreate2' style={{width: '100%', height: '100%', backgroundColor: '#f7f7f7'}} id='patientInfo2'>
                  <PatientInfoSection patientNumber={2} partnerType='Female' show={show} handleChange={handleChange} valueChange={valueChange} />
                </div>
              </div>
              <h2 style={{textAlign: 'center', fontWeight: '600', backgroundColor: '#f7f7f7', padding: '10px'}}>IUI Report</h2>

              <div>
                <SectionHeader title='Patient & Request Details' />
                <FormRow
                  columns={[
                    [
                      {label: 'Collection Date', type: 'date', name: 'collectionDate', width: '250px'},
                      {label: 'Result Date', type: 'date', name: 'resultDate', width: '250px'},
                      {
                        label: 'Procedure Requested',
                        type: 'select',
                        name: 'procedureRequested',
                        width: '250px',
                        options: [
                          {value: '', label: 'Select'},
                          {value: 'IUI H', label: 'IUI H'},
                          {value: 'IUI D', label: 'IUI D'}
                        ]
                      },
                  {
                    label: 'Sample Origin',
                    type: 'select',
                    name: 'sample',
                    width: '180px',
                    options: [
                      {value: '', label: 'Select Sample'},
                      {value: 'Fresh', label: 'Fresh'},
                      {value: 'Frozen', label: 'Frozen'},
                      {value: 'fresh+Frozen', label: 'fresh+Frozen'}
                    ]
                  }
                    ],
                    [
                      {label: 'Collection Method', name: 'collectionMethod', width: '250px'},
                      {label: 'Abstinence Period', name: 'abstinencePeriod', width: '250px'},
                      {label: 'Collection Difficulty', name: 'collectionDifficulty', width: '250px'}
                    ]
                  ]}
                  show={show}
                  onChange={handleChange}
                  valueChange={valueChange}
                  defaultLabelWidth='200px'
                />
              </div>

              {/* Macroscopic Analysis */}
              <div style={{marginBottom: '20px'}}>
                <SectionHeader title='Macroscopic Examination' />
                <AnalysisTable headers={['Parameter', 'Patient Value', 'WHO Reference']} rows={macroscopicRows} show={show} onChange={handleChange} values={valueChange} commonStyles={commonStyles} />
              </div>
              {/* Microscopic Analysis */}
              <div style={{marginBottom: '20px'}}>
                <SectionHeader title='Microscopic Examination' />
                <AnalysisTable
                  headers={['Parameter', 'PRE WASH', 'POST WASH', 'WHO Reference']}
                  rows={microscopicRows}
                  show={show}
                  onChange={handleChange}
                  values={valueChange}
                  commonStyles={commonStyles}
                />
              </div>

              {/* Comments */}
              <div style={{marginBottom: '10px'}}>
                <div style={{margin: '8px 0', fontSize: '16px'}}>
                  <SectionHeader title='📋 Comments' inline={true} />
                </div>
                {!show ? (
                  <textarea
                    name='comments'
                    value={valueChange.comments}
                    onChange={handleChange}
                    style={{
                      ...commonStyles.input,
                      width: '100%',
                      minHeight: '35px',
                      padding: '6px',
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '12px',
                      marginTop: '3px'
                    }}
                    placeholder='Enter clinical comments and recommendations...'
                  />
                ) : (
                  valueChange.comments && (
                    <div
                      style={{
                        border: '1px solid #ddd',
                        padding: '6px',
                        minHeight: '25px',
                        backgroundColor: '#f9f9f9',
                        whiteSpace: 'pre-wrap',
                        fontFamily: 'Arial, sans-serif',
                        fontSize: '12px',
                        marginTop: '3px'
                      }}>
                      {valueChange.comments}
                    </div>
                  )
                )}
              </div>
              <RenderSignature show={show} valueChange={valueChange} handleChange={handleChange} commonStyles={commonStyles} />          
              <div
                style={{
                  overflowX: 'hidden',
                  maxWidth: '97%'
                  // margin: "64px auto 32px auto",
                }}>
                <div
                  style={{
                    padding: '16px 8px 16px 8px'
                  }}>
                  <span
                    style={{
                      marginLeft: '65%',
                      display: 'block',
                      marginBottom: '16px',
                      fontWeight: '600'
                    }}>
                    {/* <pre style={{fontFamily: 'system-ui', fontWeight: 500}}>Clinician Signature Andrologist Signature</pre> */}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      marginBottom: '16px',
                      fontSize: '12px'
                    }}>
                    <strong>*Percentage respect to total number of abnormal spermatoza</strong>
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
        {/* </div> */}
        {/* <p style={{textAlign:"center",padding:"30px 10px"}}>x x x End Of Bill x x x</p> */}

        <div className='Footer'>
          <button onClick={handletest} className='Button_Create'>
            Add Report
          </button>
        </div>
      </div>
    </>
  );
};

export default IUIReport;
